import React from 'react';
import { Chip, Tooltip } from '@heroui/react';
import { ResponsiveTooltip, Typography } from '../../../../components';
import { ProposalData } from '../types';
import { getPlatformIconForRenderCustom } from '../utils';
import { CalendarIcon, InfoCircle, InfoIcon } from '../../../../components/icons';
import { useTranslations } from '../../../../contexts/translation.context';

interface DescriptionCellProps {
  proposal: ProposalData;
}

const DescriptionCell: React.FC<DescriptionCellProps> = ({ proposal }) => {
  const { t } = useTranslations();

  const formatTimeAgo = (dateString: string, t: any) => {
    // Если сервер отдает локальное время с 'Z', нужно добавить offset
    const utcDate = new Date(dateString);
    if (Number.isNaN(utcDate.getTime())) {
      return '';
    }
    const date = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();

    if (diffInMs < 0) {
      return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(date);
    }

    const diffInHours = Math.max(0, Math.floor(diffInMs / (1000 * 60 * 60)));
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInDays > 0) {
      // Manually format for proper Russian/English display
      const template = t('proposals.timeAgo.daysHours');
      return template
        .replace('{{days}}', diffInDays.toString())
        .replace('{{hours}}', (diffInHours % 24).toString());
    }

    if (diffInHours > 0) {
      const template = t('proposals.timeAgo.hours');
      return template.replace('{{hours}}', diffInHours.toString());
    }

    if (diffInMinutes > 0) {
      const template = t('proposals.timeAgo.minutes');
      return template.replace('{{minutes}}', diffInMinutes.toString());
    }

    return t('proposals.timeAgo.justNow');
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2">
          {proposal?.campaign_types?.map((type: string, index: number) => (
            <Typography
              key={index}
              variant="label3"
              className={`!font-semibold ${type === 'free' ? 'uppercase' : 'capitalize'}`}
            >
              {type}
            </Typography>
          ))}
        </div>
        <ResponsiveTooltip
          content={proposal.requirements || t('proposals.noRequirements')}
          placement="top"
          className="max-w-[18.75rem] rounded-2 py-2 !px-4"
        >
          <div className="flex items-center gap-1 px-1.5 py-0.5 bg-neutral-12 rounded-full cursor-pointer hover:bg-[#e0e0e0] transition-colors">
            <span className="text-xs font-regular text-auth-muted">
              {t('proposals.requirements')}
            </span>
            <InfoCircle />
          </div>
        </ResponsiveTooltip>
      </div>
      <div className="text-sm text-auth-ink leading-[1.125rem] line-clamp-2">
        {proposal.description}
      </div>
      <div className="flex items-center gap-1">
        <CalendarIcon />
        <span className="text-xs text-auth-muted">{formatTimeAgo(proposal.createdAt, t)}</span>
      </div>
    </div>
  );
};

export default DescriptionCell;
